import type {
  EnvironmentId,
  PullRequestActivity,
  PullRequestDetail,
  PullRequestRef,
} from "@cmmd-forge/contracts";
import { Button } from "~/components/ui/button";
import { cmmdApiClient } from "~/environments/cmmdApi";
import { commandPullRequestRef } from "~/lib/pullRequestReactQuery";
import { canEditPullRequestComment } from "../pullRequestEditing.logic";
import { usePullRequestHandoffController } from "~/pullRequest/handoff/PullRequestHandoffProvider";
import { PullRequestReactionPills } from "./PullRequestReactionPills";
import { PullRequestReviewThreadRow } from "./PullRequestReviewThread";
import {
  PullRequestTimelineEmpty,
  PullRequestTimelineError,
  PullRequestTimelineLoading,
} from "./PullRequestTimelineStates";
import { PullRequestTimelineItem } from "./PullRequestTimelineItem";
import { usePullRequestSerializedCommand } from "./usePullRequestSerializedCommand";
import {
  buildPullRequestTimelineEntries,
  type PullRequestTimelineEntry,
} from "./pullRequestTimelineOrdering.logic";
import {
  pullRequestTimelineAuthor,
  pullRequestTimelineCommentAuthor,
  pullRequestTimelineCommentEditingEnabled,
  pullRequestTimelineCommentTitle,
  pullRequestTimelineReactionsEnabled,
  pullRequestTimelineReplyEnabled,
  pullRequestTimelineResolutionEnabled,
} from "./pullRequestTimelinePermissions.logic";

export interface PullRequestTimelineProps {
  readonly detail: PullRequestDetail;
  readonly isDetailFresh: boolean;
  readonly environmentId: EnvironmentId;
  readonly reference: PullRequestRef;
  readonly activity: PullRequestActivity | undefined;
  readonly pending: boolean;
  readonly error: Error | null;
  readonly order: "newest" | "oldest";
  readonly onRetry: () => void;
}

export function PullRequestTimeline(props: PullRequestTimelineProps) {
  const reactionCommand = usePullRequestSerializedCommand(props.environmentId, props.reference);
  const timelineCommand = usePullRequestSerializedCommand(props.environmentId, props.reference);
  const state = resolveTimelineState(props);
  return (
    <PullRequestTimelineState
      {...props}
      {...state}
      reactionCommand={reactionCommand}
      timelineCommand={timelineCommand}
    />
  );
}

type TimelineState =
  | { readonly kind: "loading" }
  | { readonly kind: "error"; readonly error: Error }
  | { readonly kind: "empty" }
  | { readonly kind: "ready"; readonly activity: PullRequestActivity };

function resolveTimelineState({
  activity,
  pending,
  error,
}: PullRequestTimelineProps): TimelineState {
  if (activity !== undefined) return { kind: "ready", activity };
  if (error !== null) return { kind: "error", error };
  return pending ? { kind: "loading" } : { kind: "empty" };
}

function PullRequestTimelineState(
  props: PullRequestTimelineProps &
    TimelineState & {
      readonly reactionCommand: ReturnType<typeof usePullRequestSerializedCommand>;
      readonly timelineCommand: ReturnType<typeof usePullRequestSerializedCommand>;
    },
) {
  if (props.kind === "loading") return <PullRequestTimelineLoading />;
  if (props.kind === "error")
    return <PullRequestTimelineError error={props.error} onRetry={props.onRetry} />;
  if (props.kind === "empty") return <PullRequestTimelineEmpty />;
  return <PullRequestTimelineContent {...props} activity={props.activity} />;
}

function PullRequestTimelineContent({
  detail,
  isDetailFresh,
  environmentId,
  reference,
  activity,
  reactionCommand,
  timelineCommand,
  order,
}: PullRequestTimelineProps & {
  readonly activity: PullRequestActivity;
  readonly reactionCommand: ReturnType<typeof usePullRequestSerializedCommand>;
  readonly timelineCommand: ReturnType<typeof usePullRequestSerializedCommand>;
}) {
  const reactionsEnabled = pullRequestTimelineReactionsEnabled(detail, isDetailFresh);
  const canEditComments = pullRequestTimelineCommentEditingEnabled(detail, isDetailFresh);
  const entries = buildPullRequestTimelineEntries(detail, activity, order);
  return (
    <section className="space-y-3 px-4 py-4" aria-label="Pull request timeline">
      <TimelineTruncationNotice activity={activity} />
      <PullRequestReactionPills
        reactions={activity.reactions}
        enabled={reactionsEnabled}
        pending={reactionCommand.isPending}
        onToggle={(content, reacted) =>
          reactionCommand.mutate(async () => {
            await cmmdApiClient.setPullRequestReaction({
              ...commandPullRequestRef(reference),
              idempotencyKey: crypto.randomUUID(),
              content,
              reacted,
            });
          })
        }
      />
      {entries.map((entry) => (
        <PullRequestTimelineEntryRow
          key={entry.id}
          entry={entry}
          detail={detail}
          environmentId={environmentId}
          reference={reference}
          canEditComments={canEditComments}
          canReply={pullRequestTimelineReplyEnabled(detail, isDetailFresh)}
          canResolve={pullRequestTimelineResolutionEnabled(detail, isDetailFresh)}
          reactionsEnabled={reactionsEnabled}
          reactionCommand={reactionCommand}
          timelineCommand={timelineCommand}
        />
      ))}
    </section>
  );
}

function PullRequestTimelineEntryRow({
  entry,
  detail,
  environmentId,
  reference,
  canEditComments,
  canReply,
  canResolve,
  reactionsEnabled,
  reactionCommand,
  timelineCommand,
}: {
  readonly entry: PullRequestTimelineEntry;
  readonly detail: PullRequestDetail;
  readonly environmentId: EnvironmentId;
  readonly reference: PullRequestRef;
  readonly canEditComments: boolean;
  readonly canReply: boolean;
  readonly canResolve: boolean;
  readonly reactionsEnabled: boolean;
  readonly reactionCommand: ReturnType<typeof usePullRequestSerializedCommand>;
  readonly timelineCommand: ReturnType<typeof usePullRequestSerializedCommand>;
}) {
  if (entry.kind === "opened") {
    return (
      <PullRequestTimelineItem
        actor={pullRequestTimelineAuthor(detail)}
        at={entry.at}
        title="opened this pull request"
      />
    );
  }
  if (entry.kind === "commit") return <PullRequestCommitTimelineItem commit={entry.commit} />;
  if (entry.kind === "comment") {
    return (
      <PullRequestCommentTimelineItem
        comment={entry.comment}
        detail={detail}
        reference={reference}
        canEditComments={canEditComments}
        reactionsEnabled={reactionsEnabled}
        reactionCommand={reactionCommand}
        timelineCommand={timelineCommand}
      />
    );
  }
  if (entry.kind === "thread") {
    return (
      <PullRequestReviewThreadRow
        environmentId={environmentId}
        reference={reference}
        thread={entry.thread}
        canReply={canReply}
        canResolve={canResolve}
        reactionsEnabled={reactionsEnabled}
      />
    );
  }
  return (
    <PullRequestTimelineItem
      actor=""
      at={entry.at}
      title={entry.id === "merged" ? "Pull request merged" : "Pull request closed"}
    />
  );
}

function TimelineTruncationNotice({ activity }: { readonly activity: PullRequestActivity }) {
  return activity.commentsTruncated ? (
    <p className="rounded-md border border-warning-border bg-warning-surface px-3 py-2 text-sm text-warning-foreground">
      Showing {activity.comments.length} of {activity.commentCount} comments. Refresh on the
      provider for the complete conversation.
    </p>
  ) : null;
}

function PullRequestCommitTimelineItem({
  commit,
}: {
  readonly commit: PullRequestActivity["commits"][number];
}) {
  return (
    <PullRequestTimelineItem
      actor={commit.authors?.[0]?.login ?? "Unknown author"}
      at={commit.committedDate}
      title={`committed ${commit.oid.slice(0, 7)}`}
      body={commit.messageHeadline || null}
    />
  );
}

function PullRequestCommentTimelineItem({
  comment,
  detail,
  reference,
  canEditComments,
  reactionsEnabled,
  reactionCommand,
  timelineCommand,
}: {
  readonly comment: PullRequestActivity["comments"][number];
  readonly detail: PullRequestDetail;
  readonly reference: PullRequestRef;
  readonly canEditComments: boolean;
  readonly reactionsEnabled: boolean;
  readonly reactionCommand: ReturnType<typeof usePullRequestSerializedCommand>;
  readonly timelineCommand: ReturnType<typeof usePullRequestSerializedCommand>;
}) {
  return (
    <PullRequestTimelineItem
      actor={pullRequestTimelineCommentAuthor(comment.author?.login)}
      at={comment.createdAt}
      title={pullRequestTimelineCommentTitle(comment.kind)}
      body={comment.body}
      markdown
      reactions={comment.reactions}
      reactionsEnabled={reactionsEnabled}
      reactionsPending={reactionCommand.isPending}
      canEdit={canEditPullRequestComment({
        canEditComments,
        viewer: detail.viewer,
        author: comment.author?.login,
        kind: comment.kind,
      })}
      editPending={timelineCommand.isPending}
      action={<ReviewRemarkFixAction comment={comment} />}
      onSave={(body) =>
        timelineCommand.execute(async () => {
          await cmmdApiClient.updatePullRequestComment({
            ...commandPullRequestRef(reference),
            idempotencyKey: crypto.randomUUID(),
            commentId: comment.id,
            kind: comment.kind as "issue-comment" | "review-comment",
            body,
          });
        })
      }
      onToggleReaction={(content, reacted) =>
        reactionCommand.mutate(async () => {
          await cmmdApiClient.setPullRequestReaction({
            ...commandPullRequestRef(reference),
            idempotencyKey: crypto.randomUUID(),
            subjectId: comment.id,
            content,
            reacted,
          });
        })
      }
    />
  );
}

function ReviewRemarkFixAction({
  comment,
}: {
  readonly comment: PullRequestActivity["comments"][number];
}) {
  const handoff = usePullRequestHandoffController();
  if (handoff === null) return null;
  if (!ACTIONABLE_REMARK_KINDS.has(comment.kind)) return null;
  return (
    <Button
      type="button"
      size="sm"
      variant="ghost"
      className="mt-1"
      disabled={handoff.pendingAction !== null}
      onClick={() => void handoff.run("fix-finding", { kind: "remark", remark: comment })}
    >
      {handoff.currentThreadMatches ? "Fix in this thread" : "Fix in a new thread"}
    </Button>
  );
}

const ACTIONABLE_REMARK_KINDS = new Set(["review", "review-comment"]);
